import { memo, useCallback, useRef } from "react";
import classNames from "classnames";
import ProductList from "app/pages/Listing/ProductList/ProductList.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import "./LandingPageListing.scss";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import Loader from "app/pages/.shared/Loader";
import { FormattedMessage } from "react-intl";
import Paragraphs from "app/pages/.shared/Paragraphs";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import MerchListingActionbar from "app/pages/.shared/MerchListingActionbar/MerchListingActionbar";
import SmartDPMarketingHeadlineContainer from "app/pages/SmartDP/SmartDPMarketingHeadline/SmartDPMarketingHeadlineContainer";
import SmartDPSearchFormContainer from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchFormContainer";
import { buildSDPSearchPayload } from "app/utils/smartDPUtils";
import { useNavigate } from "react-router-dom";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import LandingPageListingHeadMetadata from "app/pages/LandingPage/LandingPageListing/LandingPageListingHeadMetadata";
import BlockSeo from "app/pages/LandingPage/BlockSeo";
import SmartDPMarketingHeroImageContainer from "app/pages/SmartDP/SmartDPMarketingHeroImage/SmartDPMarketingHeroImageContainer";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import { useFacets } from "app/features/product/productFilters";
import { useDeviceType } from "app/hooks";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { useSortedProducts } from "app/features/product/productSort";
import { sendTagOnProductsFilterOpen } from "app/utils/analytics";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";

const IMG_SIZES = {
	medium: { width: 978 },
	small: { width: 768 },
	large: { width: 1847 },
	xlarge: { width: 1847 },
};

const LandingPageListing = ({
	resolution,
	currentLandingPage = {},
	shouldHideProductPriceBanner, // // TODO temporaire voir FBR-2686
	loading,
	listingSEO = {},
	resetAllSDPProductsFilter = () => {},
	showMembershipBlock,
	landingPageMeta = {},
}) => {
	const navigate = useNavigate();
	const productListRef = useRef();
	const actionBarRef = useRef();
	const { isDesktop } = useDeviceType();
	const { track } = useAnalytics();
	const { filteredProducts } = useFacets();
	const { sortedProducts } = useSortedProducts(filteredProducts);

	const isLargeResolution = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	const currentLandingPageFooter = currentLandingPage.footer || {};
	const {
		activateRedirect = false,
		labelRedirectListing = <FormattedMessage id="landingPage.footer.redirect.cta.text" />,
		positionRedirectListing = "center",
	} = currentLandingPageFooter;

	const handleFormSubmission = useCallback(data => {
		const queryParams = buildSDPSearchPayload(data);

		resetAllSDPProductsFilter();

		navigate({ pathname: "/sdp/listing", search: queryParams });
	}, []);

	const landingPagePictureUrl = isLargeResolution
		? currentLandingPage.mainPhotoLarge?.url
		: currentLandingPage.mainPhotoSmall?.url;

	const landingPagePictureThumbnail = isLargeResolution
		? currentLandingPage.mainPhotoLarge?.thumbnail
		: currentLandingPage.mainPhotoSmall?.thumbnail;

	const layout = currentLandingPage?.layout;

	if (isEmpty(currentLandingPage)) {
		return (
			<div className="listing listing--landing-page" data-testid="listing-landing-page">
				<div className="listing__loader">
					<Loader />
				</div>
			</div>
		);
	}

	const listingLandingPageClassName = classNames({
		"listing--landing-page": true,
	});
	const smartDPSearchClassName = classNames({
		"smartdp-search__content": true,
		"smartdp-search__content-with-sdp-form": layout?.showSmartDPForm,
	});

	const smartDPContainerClassName = classNames({
		"smartdp-search__container": true,
		"smartdp-search__container-with-filter": layout?.showSmartDPForm,
	});

	const handleFilterOpen = (filterId, isSticky) => {
		sendTagOnProductsFilterOpen({
			filterId,
			...(isDesktop &&
				typeof isSticky === "boolean" && { filterPosition: isSticky ? "sticky" : "top" }),
		});
	};

	const handleMobileFilterOpen = isSticky => {
		track("listingPage_filters_mobile_open", {
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleSortOpen = isSticky => {
		track("user_click_sort-button", {
			sortPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleScrollToTop = () => {
		if (!actionBarRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			actionBarRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });
	};

	const buttonClassName = classNames("listing__redirect-cta", {
		"listing__redirect-cta--center": positionRedirectListing === "center",
		"listing__redirect-cta--left": positionRedirectListing === "left",
		"listing__redirect-cta--right": positionRedirectListing === "right",
	});

	return (
		<div className={listingLandingPageClassName} data-testid="listing-landing-page">
			<LandingPageListingHeadMetadata
				landingPageLabel={currentLandingPage.label}
				metaTitle={landingPageMeta?.metaTitle}
				metaDescription={landingPageMeta?.metaDescription}
			/>

			<>
				<div className={smartDPContainerClassName}>
					{landingPagePictureUrl && (
						<div
							className="smartdp-search__background-image"
							data-testid="main-picture"
						>
							<SmartDPMarketingHeroImageContainer
								heroImageMobileThumbnail={landingPagePictureThumbnail}
								heroImageDesktopThumbnail={landingPagePictureThumbnail}
								heroImageMobile={landingPagePictureUrl}
								heroImageDesktop={landingPagePictureUrl}
								backgroundPosition="center"
								isBackgroundProgressiveImg={true}
								sizes={IMG_SIZES}
							/>
						</div>
					)}
					<div className={smartDPSearchClassName}>
						<SmartDPMarketingHeadlineContainer
							heroTitleDesktop={layout?.merchandisingTitleDesktop}
							heroTitleMobile={layout?.merchandisingTitleMobile}
							heroSubtitleDesktop={layout?.merchandisingSubtitleDesktop}
							heroSubtitleMobile={layout?.merchandisingSubtitleMobile}
							heroTextAlign={layout?.heroTextAlign}
						/>
						{layout?.showSmartDPForm && (
							<div className="smartdp-search__form">
								<SmartDPSearchFormContainer onSuccess={handleFormSubmission} />
							</div>
						)}
					</div>
				</div>

				<div className="listing__landing-page-header" ref={actionBarRef}>
					<MerchListingActionbar
						label={currentLandingPage?.label}
						fromPrice={currentLandingPage?.lowestPrice}
					/>
				</div>

				{currentLandingPage?.layout?.introduction && (
					<div className="listing__introduction" data-testid="listing-introduction">
						<Paragraphs
							paragraphs={currentLandingPage.layout.introduction}
							enableHTMLFormatting
						/>
					</div>
				)}

				{currentLandingPage?.displayOptions?.showFilters && (
					<ActionBar
						showMiniSearch={false}
						showMapButton={false}
						isLoading={false}
						onFilterOpen={handleFilterOpen}
						onFilterTriggerMobileOpen={handleMobileFilterOpen}
						onSortOpen={handleSortOpen}
						onSubmit={handleScrollToTop}
						onSortChange={handleSortChange}
					/>
				)}

				<div className="listing__landing-page-products" ref={productListRef}>
					<div className="listing__container">
						<div className="listing__product-list">
							<ProductList
								products={sortedProducts}
								hidePriceBanner={shouldHideProductPriceBanner}
								hideMembershipBlock={!showMembershipBlock}
							/>
						</div>

						{Boolean(activateRedirect) && (
							<div className="listing__redirect" data-testid="listing-redirect">
								<Button
									variant={BUTTON_VARIANTS.STROKE}
									size={BUTTON_SIZES.LARGE}
									label={labelRedirectListing}
									className={buttonClassName}
									data-testid="listing-redirect-cta"
									to={{ pathname: "/listing" }}
									onClick={() => track("merch_listing_redirect_cta_clicked")}
								/>
							</div>
						)}

						{loading && (
							<div className="listing__loader">
								<Loader />
							</div>
						)}
					</div>
				</div>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>
			</>

			<ScrollToTopButton />

			<div className="listing__rtb">
				<div className="listing__container">
					<ReasonToBookBlockContainer />
				</div>
			</div>

			<BlockSeo
				blockTextTitle={currentLandingPage?.blockTextSeo?.title}
				blockTextDescription={currentLandingPage?.blockTextSeo?.description}
				blockLinkTitle={currentLandingPage?.blockLinkSeo?.title}
				blockLinks={currentLandingPage?.blockLinkSeo?.links}
			/>
			<PushNotificationContainer />
		</div>
	);
};

LandingPageListing.propTypes = {
	loading: PropTypes.bool,
	resolution: PropTypes.string,
	currentLandingPage: PropTypes.object,
	shouldHideProductPriceBanner: PropTypes.bool,
	listingSEO: PropTypes.object,
	resetAllSDPProductsFilter: PropTypes.func,
	showMembershipBlock: PropTypes.bool,
	landingPageMeta: PropTypes.object,
};

export default memo(LandingPageListing);
